import React from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import type { SupplierCompanyProfile } from '../../types/types';
import { CountrySelect } from '../common/CountrySelect';
import SupplierProfileBuilderCountryBadge from './SupplierProfileBuilderCountryBadge';

interface Operations {
  employee_count: number | string;
  revenue_range: string;
}

interface SupplierProfileBuilderStepOperationsProps {
  operations: Operations;
  setOperations: React.Dispatch<React.SetStateAction<Operations>>;
  profile: SupplierCompanyProfile;
  setProfile: React.Dispatch<React.SetStateAction<SupplierCompanyProfile>>;
  opCountries: string[];
  countries: { id: number; name: string; code: string }[];
  tempCountry: string;
  setTempCountry: React.Dispatch<React.SetStateAction<string>>;
  errors: { [key: string]: string };
  setChangesSaved: React.Dispatch<React.SetStateAction<boolean>>;
  addCountry: () => void;
  removeCountry: (id: string) => void;
}

const SupplierProfileBuilderStepOperations: React.FC<
  SupplierProfileBuilderStepOperationsProps
> = ({
  operations,
  setOperations,
  profile,
  setProfile,
  opCountries,
  countries,
  tempCountry,
  setTempCountry,
  errors,
  setChangesSaved,
  addCountry,
  removeCountry,
}) => {
  return (
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Employee Count</Form.Label>
            <Form.Control
              type="number"
              value={operations.employee_count || ''}
              onChange={(e) => {
                setOperations({
                  ...operations,
                  employee_count: e.target.value,
                });
                setChangesSaved(false);
              }}
            />
            {errors.employee_count && (
              <Form.Text className="text-danger">
                {errors.employee_count}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Revenue Range</Form.Label>
            <Form.Select
              value={operations.revenue_range || ''}
              onChange={(e) => {
                setOperations({
                  ...operations,
                  revenue_range: e.target.value,
                });
                setChangesSaved(false);
              }}
            >
              <option value="">Select Range...</option>
              <option value="0-1M">$0 - $1M</option>
              <option value="1M-10M">$1M - $10M</option>
              <option value="10M+">$10M+</option>
            </Form.Select>
            {errors.revenue_range && (
              <Form.Text className="text-danger">
                {errors.revenue_range}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Add Countries of Operation</Form.Label>
            <Row>
              <Col md={10}>
                <CountrySelect
                  full
                  value={tempCountry}
                  onChange={(countryCode) =>
                    setTempCountry((countryCode as string) || '')
                  }
                />
                <label className="form-text">
                  Select a country and click "Add" to include it in your
                  profile.
                </label>
              </Col>
              <Col md={2}>
                <Button
                  variant={!tempCountry ? 'outline-primary' : 'primary'}
                  onClick={addCountry}
                  disabled={!tempCountry}
                >
                  Add
                </Button>
              </Col>
            </Row>
            {errors.opCountries && opCountries.length === 0 && (
              <Form.Text className="text-danger">
                {errors.opCountries}
              </Form.Text>
            )}
          </Form.Group>

          <div className="mb-4 d-flex flex-wrap gap-2">
            {opCountries.map((countryId) => (
              <SupplierProfileBuilderCountryBadge
                key={countryId}
                countryId={countryId}
                countryName={
                  countries.find((c) => c.code === countryId)?.name ||
                  countryId
                }
                removeCountry={removeCountry}
              />
            ))}
          </div>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Year Founded</Form.Label>
            <Form.Control
              type="text"
              value={profile?.founded_year || ''}
              onChange={(e) => {
                setProfile((prev) => ({
                  ...prev,
                  founded_year: e.target.value as unknown as number,
                }));
                setChangesSaved(false);
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default SupplierProfileBuilderStepOperations;
